import React, { memo, useEffect, useMemo, useState } from "react";
import {
	FaCheck,
	FaChevronLeft,
	FaPaperPlane,
	FaPrint,
	FaRotateLeft,
} from "react-icons/fa6";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// components
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import EmptyRecord from "../empty-record/empty-record.component";
import Spinner from "../spinner/spinner.component";
import DraftPreviewPanel from "./draft-preview-panel.component";
import DraftSearchBox from "./draft-search-box.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import { Button } from "../ui/button";
import { capitalizeWords, highlightMatch } from "./highlight-match.util";
import { filterDraftsBySearch } from "./draft-search.util";

// global functions
import { isEmptyObject, fromAdToBs } from "../../global/function";
import { useDebounce } from "../../hooks/useDebounce";

// queries
import { useQueryClient } from "@tanstack/react-query";
import {
	useCompanyDetails,
	useActiveExternalApp,
} from "../../queries/company.queries";
import {
	useDrafts,
	useUpdateDraft,
	deriveDraftLists,
	draftsQueryKey,
} from "../../queries/drafts.queries";
import type { Draft, UpdateDraftVars } from "../../queries/drafts.queries";
import {
	billDatesQueryKey,
	useAddDraftBill,
	handleBillSubmitError,
} from "../../queries/bill.queries";
import type {
	BillDatesResult,
	AddDraftBillVars,
} from "../../queries/bill.queries";
import type { InvoiceFormValues } from "../invoice-form/invoice-form.types";

// hooks
import { useIrdStatus } from "../../hooks/useIrdStatus";
import { useSession } from "../../queries/auth.queries";

interface ReadyDraftListProps {
	recentBillDate: string | false;
	viewVisibleList: "draft" | "ready";
	setViewVisibleList: () => void;
}

function ReadyDraftList({
	recentBillDate,
	viewVisibleList,
	setViewVisibleList,
}: ReadyDraftListProps) {
	const { data } = useDrafts();
	const { errorDraftsList, readyDraftsList } = deriveDraftLists(data);
	const updateDraftMutation = useUpdateDraft();
	const addDraftBillMutation = useAddDraftBill();
	const queryClient = useQueryClient();

	const { data: session } = useSession();
	const { data: companyData } = useCompanyDetails();
	const companyDetails = companyData?.company;
	const { irdStatus, irdStatusLoading, checkStatus } = useIrdStatus();
	const { billModel: activeBillModel } = useActiveExternalApp();
	const isCreationBlocked = activeBillModel === "A";

	const [tempViewModal, setTempViewModal] = useState(false);
	const [printDialogOpen, setPrintDialogOpen] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState<
		Draft | Record<string, never>
	>({});

	const [hasFailedInvoice, setHasFailedInvoice] = useState(false);
	// Replaces useUpdateDraft's former toast — success flashes inline on the
	// list (the preview panel closes right after a revert/ready-to-draft
	// action, so there's nothing to show a result *inside*).
	const [successNotice, setSuccessNotice] = useState<string | null>(null);

	const [searchQuery, setSearchQuery] = useState("");
	const debouncedQuery = useDebounce(searchQuery, 150);

	const filteredReadyDrafts = useMemo(
		() => filterDraftsBySearch(readyDraftsList, debouncedQuery),
		[readyDraftsList, debouncedQuery],
	);

	const navigate = useNavigate();

	useEffect(() => {
		setHasFailedInvoice(
			Array.isArray(errorDraftsList) && errorDraftsList.length > 0,
		);
	}, [errorDraftsList]);

	const handleTemplatePreview = (invoice: Draft) => {
		if (hasFailedInvoice) {
			if (invoice?.draft_bill_status === "failed") {
				setTempViewModal(true);
				setSelectedInvoice(invoice);
			}
		} else {
			setSelectedInvoice(invoice);
			setTempViewModal(true);
		}
	};

	const handleTemplatePreviewClose = () => {
		setSelectedInvoice({});
		setTempViewModal(false);
	};

	const handleIRDStatusCheck = async () => {
		if (irdStatusLoading || companyDetails?.company_data_corrupt) return;
		await checkStatus();
	};

	const handleBulkBillsCreate = async () => {
		if (
			addDraftBillMutation.isPending ||
			companyDetails?.company_data_corrupt ||
			isCreationBlocked
		)
			return;

		const lastBillDate = moment().format("YYYY-MM-DD");

		const billToAdd: AddDraftBillVars = {
			bills: readyDraftsList as unknown as Record<string, unknown>[],
			username: session?.username,
			password: session?.password,
			seller_pan: session?.registration_number,
			user_role: session?.user_role,
		};

		try {
			await addDraftBillMutation.mutateAsync(billToAdd);
			queryClient.setQueryData<BillDatesResult | undefined>(
				billDatesQueryKey,
				(old) => old && { ...old, lastInvoiceDate: lastBillDate },
			);
			// The old addDraftBill thunk only refreshed draftsReducer, which
			// nothing reads anymore now that draft-list/ready-draft-list are on
			// useDrafts() — refresh the actual query cache here instead.
			queryClient.invalidateQueries({ queryKey: draftsQueryKey });
			// useAddDraftBill has no onSuccess of its own — without this the
			// bills list page can still show its pre-creation cache after
			// navigating here from elsewhere if that cache is under staleTime old.
			queryClient.invalidateQueries({ queryKey: ["bills"] });
			setSuccessNotice("All bills created successfully");
		} catch (error) {
			handleBillSubmitError(error, navigate);
		}
	};

	const handleFailedToReady = async (
		billData: Partial<InvoiceFormValues> & Record<string, unknown>,
	) => {
		const drafts: UpdateDraftVars = {
			drafts: [{ ...billData } as Partial<Draft>],
			draft_bill_status: "ready",
		};
		try {
			await updateDraftMutation.mutateAsync(drafts);
			setSuccessNotice("Draft marked ready");
		} catch {
			return; // FormError (bound to updateDraftMutation) renders the message, panel stays open
		}
		handleTemplatePreviewClose();
	};

	const handleBillCreate = async (billData: Draft, saveTo?: string) => {
		if (
			addDraftBillMutation.isPending ||
			companyDetails?.company_data_corrupt
		)
			return;
		const lastBillDate = billData?.invoice_date;
		const billToAdd: AddDraftBillVars = {
			bills: [billData as unknown as Record<string, unknown>],
		};

		try {
			await addDraftBillMutation.mutateAsync(billToAdd);
			queryClient.setQueryData<BillDatesResult | undefined>(
				billDatesQueryKey,
				(old) => old && { ...old, lastInvoiceDate: lastBillDate },
			);
			queryClient.invalidateQueries({ queryKey: draftsQueryKey });
			queryClient.invalidateQueries({ queryKey: ["bills"] });
		} catch (error) {
			handleBillSubmitError(error, navigate);
		}
		handleTemplatePreviewClose();
	};

	// bulk draft update from ready to draft status
	const handleBulkReadyToDraft = async () => {
		const drafts: UpdateDraftVars = {
			drafts:
				errorDraftsList.length > 0
					? [...errorDraftsList]
					: [...readyDraftsList],
			draft_bill_status: "draft",
		};
		try {
			await updateDraftMutation.mutateAsync(drafts);
			setSuccessNotice("Reverted to draft");
		} catch {
			// FormError (bound to updateDraftMutation) renders the message
		}
	};

	// individual draft update from ready to draft status
	const handleInvoiceReadyToDraft = async (
		invoiceDraft: Draft | Record<string, never>,
	) => {
		if (isEmptyObject(invoiceDraft)) return;
		const drafts: UpdateDraftVars = {
			drafts: [{ ...invoiceDraft }],
			draft_bill_status: "draft",
		};
		try {
			await updateDraftMutation.mutateAsync(drafts);
			setSuccessNotice("Reverted to draft");
		} catch {
			return; // FormError (bound to updateDraftMutation) renders the message, panel stays open
		}
		handleTemplatePreviewClose();
	};

	const bulkIRDRequest = useMemo(() => {
		if (Array.isArray(readyDraftsList) && readyDraftsList.length > 0) {
			const hasFailedStatus = readyDraftsList.some(
				(obj) => obj.draft_bill_status !== "ready",
			);
			//returning false when there are NON Ready drafts ie failer or locked
			//This handles if drafts are ready for billing stage
			return !hasFailedStatus;
		}
		return false;
	}, [readyDraftsList]);

	const individualIRDRequest = useMemo(() => {
		if (Array.isArray(readyDraftsList) && readyDraftsList.length > 0) {
			return irdStatus;
		}
		return false;
	}, [readyDraftsList, irdStatus]);

	function renderHeaderMessage() {
		if (companyDetails?.company_data_corrupt) {
			return (
				<div className="invoice-header-message">
					There are
					<span style={{ color: "#e74c3c" }}>
						{" "}
						technical errors with your IRD account.{" "}
					</span>
					Bills cannot be created. Please contact your service
					providers to resolve this issue.
				</div>
			);
		} else if (!irdStatus) {
			return (
				<div className="invoice-header-message">
					Bill generation is currently not possible due to an error
					with the IRD server. Please check the IRD status and try
					again later.
				</div>
			);
		} else if (readyDraftsList.length > 0 && !bulkIRDRequest) {
			return (
				<div className="invoice-header-message">
					You might have{" "}
					<span style={{ color: "#e74c3c" }}>failed</span> to generate
					bill. Resolve that before you proceed.
				</div>
			);
		} else {
			return (
				<div className="invoice-header-message">
					Only <b>Current bills</b> can be generated.
				</div>
			);
		}
	}

	return (
		<>
			{viewVisibleList === "draft" && (
				<div className="draftButtonPannel" onClick={setViewVisibleList}>
					<div className="section-title-text">Quotations</div>
					<div>{readyDraftsList.length}</div>
					<div>Open Panel</div>
					<div className="openPanel">
						<FaChevronLeft size="8em" />
					</div>
				</div>
			)}
			<div
				className={`list ${
					viewVisibleList === "draft" ? "dsp_hidden" : "dsp_shown"
				}`}
			>
				<div className="drafts-invoices-container">
					<div className="drafts-invoices-header">
						<div className="di-header-title">
							<div className="section-title-text">Quotations</div>
							<div
								style={{
									display: "flex",
									gap: "8px",
								}}
							>
								{Array.isArray(readyDraftsList) &&
									readyDraftsList.length > 0 && (
										<Button
											variant="outline"
											size="icon-sm"
											onClick={handleBulkReadyToDraft}
										>
											<FaRotateLeft />
											<span className="sr-only">
												Revert All
											</span>
										</Button>
									)}
								{irdStatus ? (
									<>
										{bulkIRDRequest && (
											<Button
												size="sm"
												onClick={handleBulkBillsCreate}
												disabled={
													addDraftBillMutation.isPending ||
													companyDetails?.company_data_corrupt ||
													isCreationBlocked
												}
											>
												{addDraftBillMutation.isPending ? (
													<>
														<Spinner
															styles={undefined}
														/>{" "}
														<span>Creating...</span>
													</>
												) : (
													<>
														<FaPaperPlane />
														<span>
															Create All Bills
														</span>
													</>
												)}
											</Button>
										)}
									</>
								) : (
									<Button
										size="sm"
										onClick={handleIRDStatusCheck}
										disabled={
											irdStatusLoading ||
											companyDetails?.company_data_corrupt
										}
									>
										{irdStatusLoading ? (
											<>
												<Spinner styles={undefined} />{" "}
												<span>Checking...</span>
											</>
										) : (
											<>
												<FaPaperPlane />
												<span>Check IRD Status</span>
											</>
										)}
									</Button>
								)}
							</div>
						</div>
						{readyDraftsList.length > 0 && (
							<DraftSearchBox
								value={searchQuery}
								onChange={setSearchQuery}
								placeholder="Search by customer or invoice #"
								resultCount={filteredReadyDrafts.length}
								totalCount={readyDraftsList.length}
							/>
						)}
						{renderHeaderMessage()}
					</div>
					<div className="draft-mutation-message">
						<FormError
							isError={updateDraftMutation.isError}
							error={updateDraftMutation.error}
							fallback="Something went wrong. Please try again."
						/>
						<InlineNotice
							message={successNotice}
							type="success"
							autoHideMs={3000}
							onDismiss={() => setSuccessNotice(null)}
						/>
					</div>
					<div className="drafts-invoices-list">
						{readyDraftsList?.length > 0 ? (
							filteredReadyDrafts.length > 0 ? (
								filteredReadyDrafts.map((readyDraft) => (
									<div
										key={readyDraft.invoice_number}
										className={"list-item-container"}
										onClick={() =>
											handleTemplatePreview(readyDraft)
										}
										aria-disabled={
											hasFailedInvoice &&
											readyDraft?.draft_bill_status !==
												"failed"
										}
									>
										<div className="list-item-container-inner">
											<div
												className="list-details"
												style={{ flex: 1 }}
											>
												<div className="details-col">
													<div className="list-maintext">
														{readyDraft.buyer_name
															? highlightMatch(
																	capitalizeWords(
																		readyDraft.buyer_name,
																	),
																	debouncedQuery,
																)
															: "[Customer Name]"}
													</div>
													<div className="list-subtext">
														Sales Date:{" "}
														<span>
															{readyDraft?.sales_date
																? `${fromAdToBs(
																		readyDraft.sales_date,
																		"DD MMMM, YYYY",
																	)} (${moment(
																		readyDraft.sales_date,
																	).format(
																		"DD MMMM, YYYY",
																	)})`
																: "[Sales Date]"}
														</span>
													</div>
												</div>
												<div className="details-divider"></div>
												<div className="details-row">
													<div className="list-smalltext">
														{highlightMatch(
															`# ${readyDraft?.invoice_number}`,
															debouncedQuery,
														)}
													</div>
													<div
														className="list-smalltext"
														style={{
															whiteSpace:
																"nowrap",
															overflow: "hidden",
															textOverflow:
																"ellipsis",
														}}
													>
														{readyDraft?.updatedAt
															? `- ${moment(
																	readyDraft.updatedAt,
																).fromNow()}`
															: null}
													</div>
												</div>
											</div>
											<div className="list-details">
												<div
													className={`bill_status ${readyDraft.draft_bill_status}`}
												>
													{
														readyDraft.draft_bill_status
													}
												</div>
												{readyDraft.draft_bill_status ===
													"ready" &&
													readyDraft?.draft_valid && (
														<div className="valid-indicator">
															<FaCheck />
															<span>
																Validated
															</span>
														</div>
													)}
											</div>
										</div>
									</div>
								))
							) : (
								<EmptyRecord
									caption={`No quotations match "${debouncedQuery}"`}
								/>
							)
						) : (
							<EmptyRecord />
						)}
					</div>
				</div>
			</div>

			<StructuredSideModal
				open={tempViewModal}
				onOpenChange={(open) => {
					if (!open) handleTemplatePreviewClose();
				}}
				title="Draft Preview"
				width={760}
				actions={
					!hasFailedInvoice ? (
						<div className="flex items-center gap-2">
							<Button
								size="sm"
								variant="outline"
								onClick={() => setPrintDialogOpen(true)}
							>
								<FaPrint /> Print
							</Button>
							<Button
								size="sm"
								variant="outline"
								onClick={() =>
									handleInvoiceReadyToDraft(selectedInvoice)
								}
							>
								<FaRotateLeft /> Revert to draft
							</Button>
							{individualIRDRequest && (
								<Button
									size="sm"
									onClick={() =>
										handleBillCreate(
											selectedInvoice as Draft,
											"bill",
										)
									}
									disabled={
										addDraftBillMutation.isPending ||
										companyDetails?.company_data_corrupt
									}
								>
									{addDraftBillMutation.isPending ? (
										<>
											<Spinner styles={undefined} />
											<span>Creating...</span>
										</>
									) : (
										<>
											<FaPaperPlane />
											<span>Create Bill</span>
										</>
									)}
								</Button>
							)}
						</div>
					) : (
						<Button
							size="sm"
							variant="outline"
							onClick={() =>
								handleInvoiceReadyToDraft(selectedInvoice)
							}
						>
							<FaRotateLeft /> Revert to Draft
						</Button>
					)
				}
			>
				<DraftPreviewPanel
					selectedInvoice={selectedInvoice}
					recentBillDate={recentBillDate}
					isUpdateError={updateDraftMutation.isError}
					updateError={updateDraftMutation.error}
					printDialogOpen={printDialogOpen}
					onPrintDialogChange={setPrintDialogOpen}
					onFailedToReady={handleFailedToReady}
				/>
			</StructuredSideModal>
		</>
	);
}

export default memo(ReadyDraftList);
